import { Badge, Group, Stack, Text } from "@mantine/core";
import { Timestamp } from "firebase/firestore";
import type { TableColumn } from "react-data-table-component";

import { Service, ServiceLastAction, ServiceLastActionActor } from "../types";

const ACTOR_LABELS: Record<ServiceLastActionActor, { label: string; color: string }> = {
    customer: { label: "Customer", color: "blue" },
    team: { label: "KFHC team", color: "gray" },
    system: { label: "System", color: "violet" },
};

/** Typesense stores the date as unix seconds; hydrated Firestore rows would carry a Timestamp. */
export const lastActionToDate = (lastAction?: ServiceLastAction): Date | null => {
    const value = lastAction?.date;
    if (value === undefined || value === null) return null;
    if (typeof value === "number") return new Date(value * 1000);
    if (value instanceof Timestamp) return value.toDate();
    const seconds = (value as { seconds?: number }).seconds;
    return typeof seconds === "number" ? new Date(seconds * 1000) : null;
};

export const ServiceLastActionCell = ({ lastAction }: { lastAction?: ServiceLastAction }) => {
    const date = lastActionToDate(lastAction);
    if (!lastAction || !date) {
        return <Text size="sm" c="dimmed">—</Text>;
    }

    const actor = ACTOR_LABELS[lastAction.actor] ?? ACTOR_LABELS.team;
    return (
        <Stack gap={2} py={4}>
            <Text size="sm">{date.toLocaleString("en-GB", { hour12: true })}</Text>
            <Group gap={6} wrap="nowrap">
                <Badge size="xs" color={actor.color} variant="light" style={{ flexShrink: 0 }}>{actor.label}</Badge>
                <Text size="xs" c="dimmed" lineClamp={1} title={`${lastAction.msg}${lastAction.by ? ` · ${lastAction.by}` : ""}`}>
                    {lastAction.msg}{lastAction.by ? ` · ${lastAction.by}` : ""}
                </Text>
            </Group>
        </Stack>
    );
};

export const serviceLastActionColumn: TableColumn<Service> = {
    name: "Latest action",
    id: "lastAction.date",
    sortable: true,
    sortField: "lastAction.date",
    selector: (row) => lastActionToDate(row.lastAction)?.getTime() ?? 0,
    cell: (row) => <ServiceLastActionCell lastAction={row.lastAction} />,
    minWidth: "230px",
    wrap: true,
};
